import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getPrefs, setPrefs } from '../lib/preferences';
import { ReactComponent as Logo } from '../assets/PM_Logo.svg';
import Overview from './Overview';
import AdminWriting from './AdminWriting';
import Boards from './Boards';
import LandingContent from './LandingContent';
import Appearance from './Appearance';

const TABS = [
  { path: 'overview',   label: 'Overview',   Icon: HomeRoundedIcon },
  { path: 'blog',       label: 'Blog',       Icon: EditNoteRoundedIcon },
  { path: 'grid',       label: 'Grid',       Icon: GridViewRoundedIcon },
  { path: 'content',    label: 'Main Page',  Icon: TuneRoundedIcon },
  { path: 'appearance', label: 'Appearance', Icon: PaletteRoundedIcon },
];

// ─── Themed MUI sx ───────────────────────────────────────────────────────────

const navItemSx = (active) => ({
  borderRadius: '8px',
  minHeight: 40,
  px: 1,
  color: active ? 'var(--text)' : 'var(--text-2)',
  bgcolor: active ? 'var(--bg-3)' : 'transparent',
  transition: 'background 0.15s ease, color 0.15s ease',
  '&:hover': { bgcolor: 'var(--bg-3)', color: 'var(--text)' },
});

const navIconSx = (active) => ({
  minWidth: 32,
  color: active ? 'var(--green)' : 'var(--text-3)',
  '& .MuiSvgIcon-root': { fontSize: 20 },
});

const navTextSx = {
  '& .MuiTypography-root': {
    fontFamily: 'var(--font-tech)',
    fontSize: '0.8rem',
    fontWeight: 500,
  },
};

const pinBtnSx = { borderRadius: '8px', width: 30, height: 30 };

const fabSx = {
  color: 'var(--text)',
  bgcolor: 'var(--bg-2)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  width: 38,
  height: 38,
  '&:hover': { bgcolor: 'var(--bg-3)', color: 'var(--green)' },
};

function initials(name, email) {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

// The sidebar rests as a slim icon rail and expands into a labelled flyout on
// hover; the pin at the top keeps it open and reflows the page instead of
// overlaying it. Same markup contract as the console this was ported from, so
// the CSS in App.css is a straight port too.
export default function Shell() {
  const { name, email, picture, signOut } = useAuth();
  const { isDark, toggle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const profileRef = useRef(null);

  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 860;
  // Desktop always boots into the rail so the pin affordance is there from the
  // first paint; on mobile the sidebar starts off-canvas.
  const [open, setOpen] = useState(() => !isMobile());
  const [pinned, setPinnedState] = useState(() => Boolean(getPrefs().navPinned));

  const setPinned = (next) => {
    setPinnedState(next);
    setPrefs({ navPinned: next });
  };

  // Close the profile popover on an outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // Escape dismisses the mobile slide-over.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && isMobile()) setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const closeOnMobile = () => { if (isMobile()) setOpen(false); };

  const ModeIcon = isDark ? LightModeRoundedIcon : DarkModeRoundedIcon;

  return (
    <div className={`sa-shell${pinned ? ' nav-pinned' : ''}`}>
      <div
        className={`sa-backdrop${open ? ' open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden={!open}
      />

      <aside
        className={`sa-sidebar ${open ? 'open' : 'collapsed'}${pinned ? ' pinned' : ''}`}
        aria-label="Admin navigation"
      >
        {/* Top rail — PM mark plus the pin control. The mark is the whole brand
            here: no name text, so the rail reads as an icon strip at rest. */}
        <div className="sa-nav-top">
          <span className="sa-nav-logo" aria-label="Parth Merchant">
            <Logo />
          </span>
          <Tooltip title={pinned ? 'Unpin navbar' : 'Keep navbar open'} placement="right">
            <IconButton
              onClick={() => setPinned(!pinned)}
              size="small"
              sx={pinBtnSx}
              className={`sa-nav-pin-btn${pinned ? ' is-pinned' : ''}`}
              aria-label={pinned ? 'Unpin navbar' : 'Keep navbar open'}
            >
              <AddRoundedIcon className="pin-ico pin-ico-collapsed" fontSize="small" />
              <PushPinRoundedIcon className="pin-ico pin-ico-expanded" fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        {/* No per-item tooltips: the rail only ever widens on hover, so by the
            time a pointer is over a row its label is already showing — a tooltip
            would just duplicate it (and linger after navigating). */}
        <List className="sa-sidebar-nav" disablePadding>
          {TABS.map(t => (
            <NavLink key={t.path} to={`/${t.path}`} className="sa-nav-link-wrap" onClick={closeOnMobile}>
              {({ isActive }) => (
                <ListItemButton disableRipple sx={navItemSx(isActive)} title={t.label}>
                  <ListItemIcon sx={navIconSx(isActive)}>
                    <t.Icon />
                  </ListItemIcon>
                  <ListItemText primary={t.label} sx={navTextSx} />
                </ListItemButton>
              )}
            </NavLink>
          ))}
        </List>

        <div className="sa-sidebar-bottom">
          {/* Light / dark switch for the console itself. The icon shows where
              you're going, not where you are. */}
          <ListItemButton
            disableRipple
            sx={navItemSx(false)}
            onClick={toggle}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <ListItemIcon sx={navIconSx(false)}>
              <ModeIcon />
            </ListItemIcon>
            <ListItemText primary={isDark ? 'Light mode' : 'Dark mode'} sx={navTextSx} />
          </ListItemButton>

          <div className="sa-user-menu" ref={profileRef}>
            {menuOpen && (
              <div className="sa-user-popover" role="menu">
                <div className="sa-user-popover-account">
                  <span className="sa-user-popover-label">Signed in as</span>
                  <span className="sa-user-popover-email">{email}</span>
                </div>
                <button type="button" className="sa-user-popover-item" role="menuitem" onClick={signOut}>
                  <LogoutRoundedIcon sx={{ fontSize: 16 }} /> Sign out
                </button>
              </div>
            )}
            <button
              type="button"
              className={`sa-user-row${menuOpen ? ' active' : ''}`}
              onClick={() => setMenuOpen(o => !o)}
              title={email}
            >
              <span className={`sa-user-avatar${picture ? ' has-photo' : ''}`}>
                {picture
                  ? <img src={picture} alt="" className="sa-user-avatar-img" referrerPolicy="no-referrer" />
                  : initials(name, email)}
              </span>
              <span className="sa-user-row-email">{email || 'Account'}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile open control — the rail is off-canvas below 860px. */}
      <Tooltip title="Open navigation" placement="right">
        <IconButton
          className="sa-open-fab"
          onClick={() => setOpen(true)}
          sx={fabSx}
          aria-label="Open navigation"
        >
          <MenuRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <main className="sa-main">
        <Routes>
          <Route path="overview" element={<Overview />} />
          <Route path="blog" element={<AdminWriting />} />
          <Route path="grid" element={<Boards />} />
          <Route path="content" element={<LandingContent />} />
          <Route path="appearance" element={<Appearance />} />
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </main>
    </div>
  );
}
